import { COLOR_GROUPS, CopySearchResponse, fileCard, FOIL_TYPES, LocationList, TYPE_GROUPS } from '@mtg/shared';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'wouter';
import { api } from '../api.ts';
import { FINISH_LABEL, plural } from '../format.ts';
import { FinishBadge } from '../components/FinishBadge.tsx';
import { useApi } from '../useApi.ts';

const FILTERS = ['name', 'set', 'color', 'type', 'finish', 'foilType', 'locationId'] as const;

/**
 * Find any card you own and where it is. Filters live in the URL, so the
 * back button and shared links keep them.
 */
export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [name, setName] = useState(params.get('name') ?? '');
  const locations = useApi(() => api('/api/locations', LocationList), []);

  // Typing updates the URL (and so the search) after a short pause.
  useEffect(() => {
    const t = setTimeout(() => set('name', name.trim()), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  function set(key: (typeof FILTERS)[number], value: string) {
    setMany({ [key]: value });
  }

  function setMany(values: Partial<Record<(typeof FILTERS)[number], string>>) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(values)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  }

  const query = new URLSearchParams();
  for (const key of FILTERS) {
    const v = params.get(key);
    if (v) query.set(key, v);
  }
  const queryString = query.toString();
  const results = useApi(
    () => (queryString ? api(`/api/copies?${queryString}`, CopySearchResponse) : Promise.resolve(null)),
    [queryString],
  );

  const select = (key: (typeof FILTERS)[number], label: string, options: { value: string; label: string }[]) => (
    <label className="field">
      <span>{label}</span>
      <select value={params.get(key) ?? ''} onChange={(e) => set(key, e.target.value)} aria-label={label}>
        <option value="">Any</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );

  const asOptions = (list: readonly string[]) => list.map((v) => ({ value: v, label: v }));

  return (
    <div className="stack">
      <h1>Search</h1>
      <input
        type="search"
        className="search-box"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Card name"
        aria-label="Card name"
        autoFocus
        autoComplete="off"
        autoCapitalize="off"
        enterKeyHint="search"
      />
      <div className="filters">
        {select('color', 'Color', asOptions(COLOR_GROUPS))}
        {select('type', 'Type', asOptions(TYPE_GROUPS))}
        <FinishSelect finish={params.get('finish') ?? ''} foilType={params.get('foilType') ?? ''} onChange={setMany} />
        {select(
          'locationId',
          'Where',
          (locations.data?.locations ?? []).map((l) => ({ value: l.id, label: l.label ? `${l.name} · ${l.label}` : l.name })),
        )}
        <label className="field">
          <span>Set</span>
          <input
            key={params.get('set') ?? ''}
            defaultValue={params.get('set') ?? ''}
            onBlur={(e) => set('set', e.target.value.trim())}
            onKeyDown={(e) => e.key === 'Enter' && set('set', e.currentTarget.value.trim())}
            placeholder="e.g. mh3"
            aria-label="Set code"
            autoCapitalize="off"
            size={6}
          />
        </label>
        {queryString && (
          <button
            type="button"
            className="btn small"
            onClick={() => {
              setName('');
              setParams({}, { replace: true });
            }}
          >
            Clear
          </button>
        )}
      </div>

      {!queryString ? (
        <p className="muted">Type part of a name, or pick a color, type, finish or place.</p>
      ) : results.error ? (
        <p className="error">{results.error}</p>
      ) : !results.data ? (
        <p className="muted">Searching…</p>
      ) : (
        <>
          <p className="muted small" role="status">
            {results.data.total === 0 ? 'No cards found.' : plural(results.data.total, 'card')}
            {results.data.truncated && ' (showing the first 300 groups; narrow the search to see the rest)'}
          </p>
          <ul className="results">
            {results.data.copies.map((c) => (
              <li key={c.id}>
                <Link href={`/cards/${c.card.scryfallId}`} className="result">
                  {c.card.imageSmall ? (
                    <img src={c.card.imageSmall} alt="" width={146} height={204} loading="lazy" />
                  ) : (
                    <span className="thumb-placeholder" />
                  )}
                  <span className="result-text">
                    <strong>
                      {c.quantity > 1 && <span className="muted">{c.quantity}× </span>}
                      {c.card.name}
                    </strong>
                    <span className="muted small">
                      {c.card.setCode.toUpperCase()} {c.card.collectorNumber}
                      <FinishBadge card={c.card} finish={c.finish} />
                      {c.lang !== 'en' && <span className="badge">{c.lang.toUpperCase()}</span>}
                    </span>
                    <span className="where">
                      {c.locationName}
                      <Divider copy={c} />
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * One Finish menu for both filters: non-foil, any foil, etched, or one kind
 * of foil (plain, surge, galaxy…), so the two can't contradict each other.
 */
function FinishSelect({ finish, foilType, onChange }: { finish: string; foilType: string; onChange: (v: { finish: string; foilType: string }) => void }) {
  const value = foilType ? `foilType:${foilType}` : finish ? `finish:${finish}` : '';
  return (
    <label className="field">
      <span>Finish</span>
      <select
        value={value}
        onChange={(e) => {
          const [key, v] = e.target.value.split(':') as ['finish' | 'foilType', string];
          onChange({ finish: key === 'finish' ? v : '', foilType: key === 'foilType' ? v : '' });
        }}
        aria-label="Finish"
      >
        <option value="">Any</option>
        <option value="finish:nonfoil">{FINISH_LABEL.nonfoil}</option>
        <option value="finish:foil">Foil, any kind</option>
        <option value="finish:etched">{FINISH_LABEL.etched}</option>
        <optgroup label="Kind of foil">
          {FOIL_TYPES.map((f) => (
            <option key={f.id} value={`foilType:${f.id}`}>
              {f.label}
            </option>
          ))}
        </optgroup>
      </select>
    </label>
  );
}

/** The divider, when the copy sits in the drawer it files in. */
function Divider({ copy }: { copy: { locationId: string; card: Parameters<typeof fileCard>[0] } }) {
  const spot = fileCard(copy.card);
  return spot.drawerId === copy.locationId ? <> · {spot.divider}</> : null;
}
